import { useEffect, useState, useCallback } from 'react';
import { Trash2, RefreshCw, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import API_BASE from '../../utils/api';
import { useAuth } from '../../auth/useAuth';

const DEFAULT_PERMISSIONS = {
  viewInvoices: true,
  viewProposals: true,
  approveProposals: true,
  viewProjectStatus: true,
  viewRetainers: true,
};

const PERMISSION_LABELS = {
  viewInvoices: 'View Invoices',
  viewProposals: 'View Proposals',
  approveProposals: 'Approve Proposals',
  viewProjectStatus: 'View Project Status',
  viewRetainers: 'View Retainers',
};

export default function ClientPortalSettings() {
  const { auth } = useAuth();
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [expandedClientId, setExpandedClientId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [inviteForm, setInviteForm] = useState({
    name: '',
    email: '',
    projectIds: [],
    permissions: { ...DEFAULT_PERMISSIONS },
  });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');

  const userId = localStorage.getItem('userId');

  const loadData = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [clientsRes, projectsRes] = await Promise.all([
        fetch(`${API_BASE}/api/client-management`, { credentials: 'include' }),
        fetch(`${API_BASE}/api/projects/${userId}`, { credentials: 'include' }),
      ]);
      const [clientsData, projectsData] = await Promise.all([
        clientsRes.ok ? clientsRes.json() : [],
        projectsRes.ok ? projectsRes.json() : [],
      ]);
      setClients(clientsData);
      setProjects(Array.isArray(projectsData) ? projectsData : []);
    } catch {
      // silently fail — UI shows empty state
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  async function handleInvite(e) {
    e.preventDefault();
    setInviteError('');
    setInviteSuccess('');
    setInviteLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/client-management/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(inviteForm),
      });
      const data = await res.json();

      if (!res.ok) {
        setInviteError(data?.error || 'Failed to send invite.');
        return;
      }

      setInviteSuccess(`Invite sent to ${inviteForm.email}.`);
      setInviteForm({ name: '', email: '', projectIds: [], permissions: { ...DEFAULT_PERMISSIONS } });
      setShowInviteForm(false);
      await loadData();
    } catch {
      setInviteError('Something went wrong. Please try again.');
    } finally {
      setInviteLoading(false);
    }
  }

  async function handleResendInvite(clientId) {
    setResendingId(clientId);
    try {
      await fetch(`${API_BASE}/api/client-management/${clientId}/resend-invite`, {
        method: 'POST',
        credentials: 'include',
      });
    } finally {
      setResendingId(null);
    }
  }

  async function handleDelete(clientId) {
    if (!window.confirm('Remove this client\'s access? This cannot be undone.')) return;
    setDeletingId(clientId);
    try {
      await fetch(`${API_BASE}/api/client-management/${clientId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      setClients((prev) => prev.filter((c) => c.clientId !== clientId));
    } finally {
      setDeletingId(null);
    }
  }

  async function handleSavePermissions(client) {
    setSavingId(client.clientId);
    try {
      await fetch(`${API_BASE}/api/client-management/${client.clientId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          permissions: client.permissions,
          projectIds: client.projectIds,
        }),
      });
    } finally {
      setSavingId(null);
    }
  }

  function updateClientField(clientId, field, value) {
    setClients((prev) =>
      prev.map((c) => (c.clientId === clientId ? { ...c, [field]: value } : c))
    );
  }

  function toggleProjectForClient(clientId, projectId) {
    setClients((prev) =>
      prev.map((c) => {
        if (c.clientId !== clientId) return c;
        const pids = Array.isArray(c.projectIds) ? c.projectIds : [];
        const updated = pids.includes(projectId)
          ? pids.filter((p) => p !== projectId)
          : [...pids, projectId];
        return { ...c, projectIds: updated };
      })
    );
  }

  function toggleInviteProject(projectId) {
    setInviteForm((prev) => {
      const pids = prev.projectIds.includes(projectId)
        ? prev.projectIds.filter((p) => p !== projectId)
        : [...prev.projectIds, projectId];
      return { ...prev, projectIds: pids };
    });
  }

  function toggleInvitePermission(key) {
    setInviteForm((prev) => ({
      ...prev,
      permissions: { ...prev.permissions, [key]: !prev.permissions[key] },
    }));
  }

  if (loading) return <p style={{ color: '#6b7280', padding: 8 }}>Loading…</p>;

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>Client Portal</h2>
          <p style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
            Invite clients to view their project documents and approve proposals.
          </p>
        </div>
        <button
          onClick={() => { setShowInviteForm((v) => !v); setInviteError(''); setInviteSuccess(''); }}
          style={primaryBtn}
        >
          <Plus size={14} />
          Invite Client
        </button>
      </div>

      {inviteSuccess && (
        <p style={{ fontSize: 13, color: '#065f46', backgroundColor: '#d1fae5', padding: '10px 14px', borderRadius: 6, marginBottom: 16 }}>
          {inviteSuccess}
        </p>
      )}

      {/* Invite form */}
      {showInviteForm && (
        <form
          onSubmit={handleInvite}
          style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '20px 24px', marginBottom: 24, background: '#fff' }}
        >
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>New Client Invite</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Name</label>
              <input
                required
                type="text"
                value={inviteForm.name}
                onChange={(e) => setInviteForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="Client name"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input
                required
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm((p) => ({ ...p, email: e.target.value }))}
                placeholder="client@email.com"
                style={inputStyle}
              />
            </div>
          </div>

          {/* Project access */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ ...labelStyle, display: 'block', marginBottom: 8 }}>Project Access</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {projects.map((p) => {
                const selected = inviteForm.projectIds.includes(p.projectId);
                return (
                  <button
                    key={p.projectId}
                    type="button"
                    onClick={() => toggleInviteProject(p.projectId)}
                    style={{
                      padding: '4px 12px',
                      borderRadius: 20,
                      border: '1px solid',
                      borderColor: selected ? '#111' : '#d1d5db',
                      backgroundColor: selected ? '#111' : '#fff',
                      color: selected ? '#fff' : '#374151',
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    {p.projectNickname || p.projectId}
                  </button>
                );
              })}
              {projects.length === 0 && (
                <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>No projects yet.</p>
              )}
            </div>
          </div>

          {/* Permissions */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ ...labelStyle, display: 'block', marginBottom: 8 }}>Permissions</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!inviteForm.permissions[key]}
                    onChange={() => toggleInvitePermission(key)}
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {inviteError && (
            <p style={{ fontSize: 13, color: '#dc2626', marginBottom: 12 }}>{inviteError}</p>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button type="submit" disabled={inviteLoading} style={primaryBtn}>
              {inviteLoading ? 'Sending…' : 'Send Invite'}
            </button>
            <button type="button" onClick={() => setShowInviteForm(false)} style={ghostBtn}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Client list */}
      {clients.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '40px 24px',
            border: '1px dashed #d1d5db',
            borderRadius: 10,
            color: '#9ca3af',
            fontSize: 14,
          }}
        >
          No clients invited yet. Use the button above to send your first invite.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {clients.map((client) => {
            const isExpanded = expandedClientId === client.clientId;
            return (
              <div
                key={client.clientId}
                style={{ border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff', overflow: 'hidden' }}
              >
                {/* Row header */}
                <div
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div>
                      <p style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>{client.name}</p>
                      <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6b7280' }}>{client.email}</p>
                    </div>
                    <StatusPill status={client.status} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {client.status === 'invited' && (
                      <button
                        onClick={() => handleResendInvite(client.clientId)}
                        disabled={resendingId === client.clientId}
                        title="Resend invite"
                        style={iconBtn}
                      >
                        <RefreshCw size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(client.clientId)}
                      disabled={deletingId === client.clientId}
                      title="Remove access"
                      style={{ ...iconBtn, color: '#dc2626' }}
                    >
                      <Trash2 size={14} />
                    </button>
                    <button
                      onClick={() => setExpandedClientId(isExpanded ? null : client.clientId)}
                      style={iconBtn}
                    >
                      {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                  </div>
                </div>

                {/* Expanded edit panel */}
                {isExpanded && (
                  <div style={{ borderTop: '1px solid #f3f4f6', padding: '16px 20px' }}>
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ ...labelStyle, display: 'block', marginBottom: 8 }}>Project Access</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {projects.map((p) => {
                          const selected = (client.projectIds || []).includes(p.projectId);
                          return (
                            <button
                              key={p.projectId}
                              type="button"
                              onClick={() => toggleProjectForClient(client.clientId, p.projectId)}
                              style={{
                                padding: '4px 12px',
                                borderRadius: 20,
                                border: '1px solid',
                                borderColor: selected ? '#111' : '#d1d5db',
                                backgroundColor: selected ? '#111' : '#fff',
                                color: selected ? '#fff' : '#374151',
                                fontSize: 13,
                                cursor: 'pointer',
                              }}
                            >
                              {p.projectNickname || p.projectId}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div style={{ marginBottom: 16 }}>
                      <label style={{ ...labelStyle, display: 'block', marginBottom: 8 }}>Permissions</label>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
                          <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={!!(client.permissions || {})[key]}
                              onChange={() =>
                                updateClientField(client.clientId, 'permissions', {
                                  ...(client.permissions || {}),
                                  [key]: !(client.permissions || {})[key],
                                })
                              }
                            />
                            {label}
                          </label>
                        ))}
                      </div>
                    </div>

                    <button
                      onClick={() => handleSavePermissions(client)}
                      disabled={savingId === client.clientId}
                      style={primaryBtn}
                    >
                      {savingId === client.clientId ? 'Saving…' : 'Save Changes'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    active: { bg: '#d1fae5', text: '#065f46', label: 'Active' },
    invited: { bg: '#fef3c7', text: '#92400e', label: 'Invited' },
    suspended: { bg: '#fee2e2', text: '#991b1b', label: 'Suspended' },
  };
  const s = map[status] || { bg: '#f3f4f6', text: '#374151', label: status };
  return (
    <span
      style={{
        fontSize: 11,
        padding: '2px 8px',
        borderRadius: 20,
        backgroundColor: s.bg,
        color: s.text,
        fontWeight: 600,
      }}
    >
      {s.label}
    </span>
  );
}

const labelStyle = { fontSize: 12, fontWeight: 600, color: '#374151' };

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  fontSize: 13,
  boxSizing: 'border-box',
};

const primaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 14px',
  backgroundColor: '#111',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const ghostBtn = {
  padding: '8px 14px',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  background: 'none',
  fontSize: 13,
  cursor: 'pointer',
};

const iconBtn = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 6,
  border: '1px solid #e5e7eb',
  borderRadius: 6,
  background: 'none',
  cursor: 'pointer',
  color: '#374151',
};
